import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import { useAuth } from "./AuthContext";
import { createDebugLogger } from "@utils/debugLog";

const log = createDebugLogger("config");

export interface Features {
  push?: boolean;
  [key: string]: boolean | undefined;
}

export interface RegionConfig {
  [key: string]: unknown;
}

export interface M365CopilotConfig {
  /** The Entra app registration the SPA signs in against. */
  clientId: string;
  /**
   * Graph permissions to ask that registration for. Absent when the deployment made no choice,
   * which leaves the SPA on its own list — not the same as asking for nothing.
   */
  scopes?: string[];
}

export interface AiAssistantConfig {
  m365Copilot?: M365CopilotConfig;
}

/**
 * What this region's MCP endpoint will actually honour.
 *
 * Read from the region rather than assumed, because the account panel asks a user to connect an
 * assistant and so has to describe what connecting grants. A region that serves no endpoint, and a
 * region that serves reads only, are different offers.
 */
export interface McpConfig {
  /** True when the region registers the endpoint at all. */
  enabled: boolean;
  /** True when the write tools are served. Never true while `enabled` is false. */
  writeEnabled: boolean;
  /** Where a user disconnects every assistant. Empty when Login's URL is not configured. */
  revokeUrl: string;
}

export interface Config {
  regionName?: string;
  regionBaseUrl: string;
  features: Features;
  aiAssistant?: AiAssistantConfig;
  mcp?: McpConfig;
  /** Where this region's browser telemetry goes. Absent when the region has none configured. */
  applicationInsightsConnectionString?: string;
}

/**
 * Reads the Microsoft 365 Copilot settings out of the region config. The client id is absent when
 * the deployment has none configured — the AI connect panel then reports the provider as
 * unavailable rather than asking the user for a registration of their own.
 */
function readM365CopilotConfig(
  regionConfig: RegionConfig,
): M365CopilotConfig | undefined {
  const ai = regionConfig.aiAssistant;
  if (typeof ai !== "object" || ai === null) return undefined;

  const m365 = (ai as Record<string, unknown>).m365Copilot;
  if (typeof m365 !== "object" || m365 === null) return undefined;

  const { clientId, scopes } = m365 as Record<string, unknown>;
  if (typeof clientId !== "string" || clientId.length === 0) return undefined;

  const parsedScopes = Array.isArray(scopes)
    ? scopes.filter((s): s is string => typeof s === "string" && s.length > 0)
    : [];

  return parsedScopes.length > 0
    ? { clientId, scopes: parsedScopes }
    : { clientId };
}

/**
 * Reads the MCP settings out of the region config. Absent when the region predates them or has MCP
 * off, which the panel treats as "nothing to connect to" rather than guessing a default.
 */
function readMcpConfig(regionConfig: RegionConfig): McpConfig | undefined {
  const mcp = regionConfig.mcp;
  if (typeof mcp !== "object" || mcp === null) return undefined;

  const { enabled, writeEnabled, revokeUrl } = mcp as Record<string, unknown>;
  if (typeof enabled !== "boolean") return undefined;

  return {
    enabled,
    // Belt and braces against a region that reports writes while serving no endpoint: the server
    // already guards this, and a client that believes it would describe access that does not exist.
    writeEnabled: enabled && writeEnabled === true,
    revokeUrl: typeof revokeUrl === "string" ? revokeUrl : "",
  };
}

export interface ConfigContextValue {
  config: Config | null;
  regionConfig: RegionConfig | null;
  isLoading: boolean;
  error: Error | null;
  refreshConfig: () => Promise<void>;
  updateFeature: (key: string, enabled: boolean) => void;
}

export const ConfigContext = createContext<ConfigContextValue | undefined>(
  undefined,
);

interface ConfigProviderProps {
  children: ReactNode;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children }) => {
  const [config, setConfig] = useState<Config | null>(null);
  const [regionConfig, setRegionConfig] = useState<RegionConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const {
    accessToken,
    isAuthenticated,
    isLoading: authLoading,
    regionBaseUrl,
    regionName,
  } = useAuth();

  const updateFeature = useCallback((key: string, enabled: boolean) => {
    setConfig((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        features: {
          ...prev.features,
          [key]: enabled,
        },
      };
    });
  }, []);

  const refreshConfig = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      if (!accessToken) {
        throw new Error("No access token available");
      }

      if (!regionBaseUrl) {
        throw new Error("Region base URL is not set");
      }

      // Build config object from auth state
      const baseConfig: Config = {
        regionName: regionName ?? undefined,
        regionBaseUrl,
        features: { push: false },
      };

      log("Config loaded from Lobby auth state", baseConfig);
      setConfig(baseConfig);

      // Now load region-specific config using the regionBaseUrl
      try {
        const response = await fetch(`${baseConfig.regionBaseUrl}/config`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          console.warn(
            "[Region ConfigService] Failed to load region config:",
            response.status,
          );
          // Still continue with base config
          return;
        }

        const regionConfigData = await response.json() as RegionConfig;
        setRegionConfig(regionConfigData);
        log("Region config loaded", regionConfigData);

        const m365Copilot = readM365CopilotConfig(regionConfigData);
        if (m365Copilot) {
          setConfig((prev) =>
            prev ? { ...prev, aiAssistant: { m365Copilot } } : prev,
          );
        }

        const mcp = readMcpConfig(regionConfigData);
        if (mcp) {
          setConfig((prev) => (prev ? { ...prev, mcp } : prev));
        }

        const { applicationInsightsConnectionString } = regionConfigData;
        if (
          typeof applicationInsightsConnectionString === "string" &&
          applicationInsightsConnectionString.length > 0
        ) {
          setConfig((prev) =>
            prev ? { ...prev, applicationInsightsConnectionString } : prev,
          );
        }
      } catch (configError) {
        console.error(
          "[Region ConfigService] Error loading region config:",
          configError,
        );
        // Still continue with base config
      }
    } catch (err) {
      console.error("[Region ConfigService] Error loading config:", err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, regionBaseUrl, regionName]);

  // Load config when authenticated
  useEffect(() => {
    if (authLoading) {
      setIsLoading(true);
      return;
    }

    if (!isAuthenticated || !accessToken || !regionBaseUrl) {
      setConfig(null);
      setRegionConfig(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    const resolvedRegionName = regionName ?? undefined;
    const hasRegionChanged =
      !!regionBaseUrl &&
      (!config ||
        config.regionBaseUrl !== regionBaseUrl ||
        config.regionName !== resolvedRegionName);

    if (isAuthenticated && accessToken && hasRegionChanged) {
      refreshConfig();
    }
  }, [
    authLoading,
    isAuthenticated,
    accessToken,
    config,
    refreshConfig,
    regionBaseUrl,
    regionName,
  ]);

  const value: ConfigContextValue = {
    config,
    regionConfig,
    isLoading,
    error,
    refreshConfig,
    updateFeature,
  };

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  );
};

export const useConfig = (): ConfigContextValue => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return context;
};
